import "./MyDay.scss";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSocket } from "../../context/SocketContext";
import { getMyDay } from "../../api/search/getMyDay";
import {
  MY_DAY_GROUPS,
  groupMyDayItems,
  formatMyDayDeadline,
} from "../../utils/myDay";
import Spinner from "../../components/Spinner/Spinner";
import useOpenItem from "../../hooks/useOpenItem";
import { translateLabel } from "../../i18n/defaultLabels";

// Socket events that can change what's on My Day
const REFRESH_EVENTS = [
  "task:created",
  "task:updated",
  "task:deleted",
  "todoList:created",
  "todoList:updated",
  "todoList:deleted",
];

function MyDay() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openingItemId, setOpeningItemId] = useState(null);
  const refreshTimerRef = useRef(null);
  const openItem = useOpenItem();
  const { socket, isConnected } = useSocket();

  const fetchItems = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setIsLoading(true);
      setError(null);
      const data = await getMyDay();
      setItems(data?.items || []);
    } catch (err) {
      console.error("Error fetching My Day:", err);
      if (!silent) setError("myDay.loadFailed");
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Keep the list in sync with changes made elsewhere
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleChange = () => {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(
        () => fetchItems({ silent: true }),
        1000,
      );
    };

    REFRESH_EVENTS.forEach((event) => socket.on(event, handleChange));
    return () => {
      clearTimeout(refreshTimerRef.current);
      REFRESH_EVENTS.forEach((event) => socket.off(event, handleChange));
    };
  }, [socket, isConnected, fetchItems]);

  const groups = useMemo(() => groupMyDayItems(items), [items]);

  const handleItemClick = useCallback(
    async (item) => {
      if (openingItemId) return;
      setOpeningItemId(item.id);
      try {
        await openItem(item);
      } catch (err) {
        console.error("Error opening My Day item:", err);
        setOpeningItemId(null);
      }
    },
    [openingItemId, openItem],
  );

  const renderCard = (item, groupKey) => {
    const isListItem = item.type === "listItem";
    return (
      <div
        key={`${item.type}-${item.id}`}
        className={`my-day-card my-day-card--${groupKey} ${openingItemId === item.id ? "is-opening" : ""}`}
        onClick={() => handleItemClick(item)}
      >
        <div className="my-day-card__type">
          <span className="material-symbols-outlined">
            {isListItem ? "format_list_bulleted" : "checklist"}
          </span>
          {isListItem
            ? t("itemType.listItem")
            : item.parentTaskTitle
              ? t("itemType.subtask")
              : t("itemType.task")}
        </div>

        <div className="my-day-card__title">{item.title}</div>
        {item.parentTaskTitle && (
          <div className="my-day-card__parent">
            {t("myDay.subtaskOf", { title: item.parentTaskTitle })}
          </div>
        )}

        <div className="my-day-card__context">
          {item.listScope === "personal" ? (
            <span>{t("itemType.personalList")}</span>
          ) : (
            <>
              <span style={{ color: item.organization?.color }}>
                {item.organization?.name}
              </span>
              <span className="my-day-card__separator">/</span>
              <span style={{ color: item.project?.color }}>
                {item.project?.name}
              </span>
            </>
          )}
          {(item.board?.name || item.listName) && (
            <>
              <span className="my-day-card__separator">/</span>
              <span>{item.board?.name || item.listName}</span>
            </>
          )}
        </div>

        <div className="my-day-card__footer">
          <span className="my-day-card__deadline">
            <span className="material-symbols-outlined">schedule</span>
            {formatMyDayDeadline(item.deadline, t, i18n.language)}
          </span>
          {item.priorityLabel && (
            <span
              className="my-day-card__badge"
              style={{ backgroundColor: item.priorityColor }}
            >
              {translateLabel(item.priorityLabel, t)}
            </span>
          )}
          {item.status && (
            <span
              className="my-day-card__badge"
              style={{ backgroundColor: item.statusColor }}
            >
              {translateLabel(item.status, t)}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="my-day-wrapper">
      <div className="my-day">
        <div className="my-day-header">
          <h1>{t("myDay.title")}</h1>
          <span className="my-day-header__date">
            {new Date().toLocaleDateString(i18n.language, {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
          </span>
        </div>

        {isLoading ? (
          <div className="my-day-state">
            <Spinner />
          </div>
        ) : error ? (
          <div className="my-day-state">
            <span className="material-symbols-outlined">error</span>
            <p>{t(error)}</p>
            <button onClick={() => fetchItems()}>{t("common.retry")}</button>
          </div>
        ) : (
          <div className="my-day-columns">
            {MY_DAY_GROUPS.map((group) => (
              <section
                key={group.key}
                className={`my-day-column my-day-column--${group.key}`}
              >
                <h2 className="my-day-column__title">
                  <span className="material-symbols-outlined">
                    {group.icon}
                  </span>
                  {t(`myDay.groups.${group.key}.title`)}
                  <span className="my-day-column__count">
                    {groups[group.key].length}
                  </span>
                </h2>
                <div className="my-day-column__cards">
                  {groups[group.key].length > 0 ? (
                    groups[group.key].map((item) => renderCard(item, group.key))
                  ) : (
                    <div className="my-day-column__empty">
                      <span className="material-symbols-outlined">
                        check_circle
                      </span>
                      {t(`myDay.groups.${group.key}.empty`)}
                    </div>
                  )}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyDay;
